'use client';

import { User } from '@/typings/user';
import {
  checkAndCreateUserOnFirestore,
  createData,
  firebaseAuth,
  firebaseMessaging,
  getQuery,
  updateData,
} from '@/utils/firebase';
import { getMessage, getUserMessage, UserOnlyErrorCodes } from '@/utils/firebaseAuth';
import { useLocalStorage } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import useRollbar from '@/hooks/useRollbar';
import { deleteCookie, setCookie } from 'cookies-next';
import {
  AuthError,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  User as AuthUser,
  RecaptchaVerifier,
  ConfirmationResult,
  signInWithPhoneNumber,
  onIdTokenChanged,
} from 'firebase/auth';
import { onSnapshot, where } from 'firebase/firestore';
import { usePathname, useRouter } from 'next/navigation';
import React, { useEffect } from 'react';
import localforage from 'localforage';
import { importFirebaseMessaging } from '@/hooks/useFirebaseMessaging';
import type { Unsubscribe } from 'firebase/messaging';
import { USER_KEY, USER_TOKEN } from '@/utils/storageKeys';

export const FIRESTORE_USER = 'firestoreUser';

export type RegisterData = {
  email: string;
  password: string;
  uid?: string;
} & Partial<User>;

const useFirebaseAuth = () => {
  const [user, setUser, clearUser] = useLocalStorage<AuthUser | null>({
    key: USER_KEY,
    defaultValue: null,
  });
  const [firestoreUser, setFirestoreUser, clearFirestoreUser] = useLocalStorage<User | null>({
    key: FIRESTORE_USER,
    defaultValue: null,
  });
  const [error, setError] = React.useState<{ login?: string; register?: string; reset?: string }>(
    {},
  );
  const [loading, setLoading] = React.useState(false);
  const [confirmation, setConfirmation] = React.useState<ConfirmationResult | null>(null);
  const [phoneNumber, setPhoneNumber] = React.useState<string | null>(null);

  const pathname = usePathname();
  const rollbar = useRollbar();
  const router = useRouter();
  const recaptchaRef = React.useRef<RecaptchaVerifier | null>(null);

  // ----- Hooks ------
  React.useEffect(() => {
    if (!user?.uid) {
      clearFirestoreUser();
      return;
    }
    const itemQuery = getQuery({
      path: ['users'],
      order: [{ field: 'name', order: 'asc' }],
      query: [where('email', '==', user.email)],
    });
    return onSnapshot(
      itemQuery,
      { includeMetadataChanges: true },
      (querySnapshot) => {
        const users = querySnapshot.docs.map((doc) => ({ ...(doc.data() as User), id: doc.id }));

        if (users.length === 0) {
          clearFirestoreUser();
        } else {
          const user = users[0];
          setFirestoreUser({
            ...user,
            authId: user.authId,
          });
        }
      },
      (error: Error) => {
        rollbar.error('useFirebaseAuth - Error on get firestore user', error);
      },
    );
  }, [user?.uid]);

  React.useEffect(() => {
    if (!firebaseAuth) return;
    return onIdTokenChanged(firebaseAuth, async (user) => {
      if (!user) {
        setUser(null);
        clearUser();
        setError({});
        deleteCookie(USER_TOKEN);
      } else {
        setUser(user);
        setError({});
        const token = await user.getIdToken();
        const maxAge = 1000 * 60 * 60 * 24 * 365; // 1 year for now
        setCookie(USER_TOKEN, token, { maxAge });
      }
    });
  }, [pathname]);

  // ----- Methods ------
  function handleAuthError(
    customMessage: string,
    err: unknown,
    options?: { withNotification?: boolean },
  ) {
    const error = err as AuthError;

    // If we show a notification we don't need to show the error in the form
    if (options?.withNotification)
      notifications.show({
        title: 'Não conseguimos concluir a ação',
        message: getUserMessage(error),
        color: 'red',
      });
    else {
      setError((state) => ({ ...state, login: getUserMessage(error) }));
      setLoading(false);
    }

    // If the error is userOnly we don't need to send it to rollbar
    if (UserOnlyErrorCodes.includes(error.code)) return;
    rollbar.error(customMessage, getMessage(error), { error });
  }

  async function handleRegister({ email, password, uid, ...data }: RegisterData) {
    setError({});
    try {
      const createdUser = await createUserWithEmailAndPassword(firebaseAuth, email, password);

      if (createdUser.user) {
        try {
          if (uid) {
            // The uid is for the "accept invite" flow
            await updateData(['users'], uid, {
              authId: createdUser.user.uid,
              acceptedInvite: true,
              email,
            });
          } else {
            // Here is for the actual signup flow
            await createData(['users'], {
              authId: createdUser.user.uid,
              email,
              ...data,
            });
          }

          // User should be logged in after registration
          await handleLogin(email, password);
        } catch (err) {
          handleAuthError('useFirebaseAuth - Error on updating user on firestore', err);
        }
      } else {
        router.push('/');
      }
    } catch (err) {
      handleAuthError('AuthProvider - Error on creating new user', err);
    }
  }

  async function handleLogin(email: string, password: string) {
    setError({});
    try {
      const user = await signInWithEmailAndPassword(firebaseAuth, email, password);
      try {
        await checkAndCreateUserOnFirestore(user);
        router.push('/admin');
        // router.refresh();
      } catch (err) {
        handleAuthError('AuthProvider - Error on checking user on Firestore', err);
      }
    } catch (err) {
      handleAuthError('AuthProvider - Falha ao fazer login', err);
    }
  }

  const handlePhoneLogin = async (phoneNumber: string) => {
    try {
      setLoading(true);
      if (recaptchaRef.current) {
        const recaptcha = document.getElementById('recaptcha-container');
        if (recaptcha) {
          recaptchaRef.current.clear();
          recaptcha.innerHTML = `<div id="recaptcha" style="display: none;"></div>`;
        }
      }
      recaptchaRef.current = new RecaptchaVerifier(
        firebaseAuth,
        // This string below is the ID of the HTML element which will render recaptcha (<div id="recaptcha-container" />)
        // An error will be thrown if no HTML element with this id exists
        'recaptcha',
        {
          size: 'invisible',
        },
      );

      const res = await signInWithPhoneNumber(firebaseAuth, phoneNumber, recaptchaRef.current);
      setPhoneNumber(phoneNumber);
      setConfirmation(res);
      setError({ login: undefined });
      setLoading(false);
      setError({});
    } catch (error) {
      handleAuthError('Error on signInWithPhoneNumber', error);
    }
  };

  const handleValidateCode = React.useCallback(
    async (code: string) => {
      if (!confirmation) {
        rollbar.error('OTP not provided');
        setError({ login: 'Código inválido' });
        return;
      }
      try {
        setLoading(true);
        const authUser = await confirmation.confirm(code);
        if (authUser?.user?.phoneNumber && authUser?.user?.uid) {
          await checkAndCreateUserOnFirestore(authUser);
          const user = firebaseAuth.currentUser;
          if (user) {
            const token = await user.getIdToken();
            const maxAge = 1000 * 60 * 60 * 24 * 365; // 1 year for now
            setCookie(USER_TOKEN, token, { maxAge });
          }
          router.push('/admin');
        }
        setLoading(false);
        setError({ login: undefined });
      } catch (error) {
        handleAuthError('Error on validating otp code', error);
      }
    },
    [router, confirmation],
  );

  async function handleLogout(doNotEraseFromLocalStorage: string[]) {
    try {
      await signOut(firebaseAuth);
      Object.keys(localStorage).forEach((key) => {
        if (!doNotEraseFromLocalStorage.includes(key)) localStorage.removeItem(key);
      });
      localforage.removeItem('FCM_TOKEN');
      router.push('/entrar');
      // router.refresh();
    } catch (err) {
      handleAuthError('AuthProvider - Error on Logout', err, { withNotification: true });
    }
  }

  async function handleResetPwd(email: string) {
    setError({});
    try {
      await sendPasswordResetEmail(firebaseAuth, email);
    } catch (err) {
      handleAuthError('AuthProvider - Error on reseting password', err);
    }
  }

  useEffect(() => {
    if (!firebaseMessaging) return;

    let unsub: Unsubscribe | null = null;
    importFirebaseMessaging()
      .then((messaging) => {
        if (!messaging) return;
        unsub = messaging.onMessage(firebaseMessaging, ({ messageId, notification }) => {
          notifications.show({
            id: messageId,
            title: notification?.title,
            message: notification?.body,
            autoClose: false,
          });
        });
      })
      .catch((err) =>
        rollbar.error('AuthProvider - Falha ao importar firebase messaging', err as Error),
      );
    if (unsub) return unsub;
  }, [firestoreUser?.id]);

  const phoneAuth = {
    handlePhoneLogin,
    handleValidateCode,
    phoneNumber,
    confirmation,
  };

  return [
    firestoreUser,
    error,
    handleLogin,
    handleLogout,
    handleRegister,
    handleResetPwd,
    loading,
    phoneAuth,
  ] as const;
};

export default useFirebaseAuth;
